import React, { useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormikContext, FieldArray } from "formik";
import SectionHeading from "../common/SectionHeading";
import { RangeField } from "../FormFields";
import DynamicFieldGroup from "../common/DynamicFieldGroup";
import RowField from "../common/RowFeild";

const MonthlyIncome = ({ formField }) => {
  const {
    salaryAmount,
    businessAmount,
    agricultureAmount,
    interestAmount,
    rentalAmounts, // ✅ updated
  } = formField;

  const { values, setFieldValue, errors, touched } = useFormikContext();
  //  console.log("form field values monthly income", values);
  const incomeOptions = [
    "Salary",
    "Business",
    "Agriculture",
    "Interest",
    "Other Earnings",
  ];
  const rentalOptions = ["Flat", "Land", "Transport"];
  const isSmallDevice = useMediaQuery("(max-width:600px)");

  // Handle checkbox toggle for income sources
  const handleCheckboxChange = (fieldName, value) => {
    let selected = values[fieldName] || [];

    if (selected.includes(value)) {
      selected = selected.filter((v) => v !== value);
      // Reset the corresponding amount when unchecked
      if (value === "Salary") setFieldValue("monthlyIncome.salary", 0);
      if (value === "Business") setFieldValue("monthlyIncome.business", 0);
      if (value === "Agriculture")
        setFieldValue("monthlyIncome.agriculture", 0);
      if (value === "Interest") setFieldValue("monthlyIncome.interest", 0);
      if (value === "Other Earnings")
        setFieldValue("monthlyIncome.otherEarnings", 0);
    } else {
      selected = [...selected, value];
    }

    setFieldValue(fieldName, selected);
  };

  // Render checkboxes for income sources using RowField
  const renderCheckboxes = (fieldName, options) =>
    options.map((option) => (
      <RowField
        key={option}
        label={option}
        checked={values[fieldName]?.includes(option) || false}
        onCheckboxChange={() => handleCheckboxChange(fieldName, option)}
        sliderValue={
          option === "Salary"
            ? values.monthlyIncome?.salary || 0
            : option === "Business"
              ? values.monthlyIncome?.business || 0
              : option === "Agriculture"
                ? values.monthlyIncome?.agriculture || 0
                : option === "Other Earnings"
                  ? values.monthlyIncome?.otherEarnings || 0
                  : values.monthlyIncome?.interest || 0
        }
        onSliderChange={(e, newValue) => {
          const field =
            option === "Salary"
              ? "monthlyIncome.salary"
              : option === "Business"
                ? "monthlyIncome.business"
                : option === "Agriculture"
                  ? "monthlyIncome.agriculture"
                  : option === "Other Earnings"
                    ? "monthlyIncome.otherEarnings"
                    : "monthlyIncome.interest";
          setFieldValue(field, newValue);
        }}
        sliderMin={0}
        sliderMax={1000000}
        sliderStep={1000}
        inputValue={
          option === "Salary"
            ? values.monthlyIncome?.salary || ""
            : option === "Business"
              ? values.monthlyIncome?.business || ""
              : option === "Agriculture"
                ? values.monthlyIncome?.agriculture || ""
                : option === "Other Earnings"
                  ? values.monthlyIncome?.otherEarnings || ""
                  : values.monthlyIncome?.interest || ""
        }
        onInputChange={(e) => {
          const newValue = Number(e.target.value) || 0;
          const field =
            option === "Salary"
              ? "monthlyIncome.salary"
              : option === "Business"
                ? "monthlyIncome.business"
                : option === "Agriculture"
                  ? "monthlyIncome.agriculture"
                  : option === "Other Earnings"
                    ? "monthlyIncome.otherEarnings"
                    : "monthlyIncome.interest";
          setFieldValue(field, newValue);
        }}
        inputLabel="Amount ₹"
        inputType="number"
      />
    ));

  return (
    <Box
      sx={{
        p: isSmallDevice ? 0.5 : 1,
        maxWidth: 1000,
        mx: "auto",
        fontSize: { xs: "0.875rem", sm: "1rem" },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: isSmallDevice ? 2 : 3,
          position: "relative",
          border: "1px solid #e0e0e0",
          borderRadius: 3,
        }}
      >
        {/* <SectionHeading title="Monthly Income Sources" number={2} /> */}

        {/* ✅ Income Checkboxes */}
        <div className="row mb-3">
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              mb: 2,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            1. Select Income Sources:
          </Typography>
          {renderCheckboxes("incomeSources", incomeOptions)}
        </div>

        {/* ✅ Rental Income Section */}
        <FieldArray name="monthlyIncome.rentalAmounts">
          {({ push, remove }) => (
            <div className="row mb-3" style={{ position: "relative" }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                2. Rental Income Sources:
              </Typography>

              {values.monthlyIncome?.rentalAmounts?.map((rental, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    position: "relative",
                    p: { xs: 2, sm: 0 },
                    // mx: isSmallDevice ? -1 : 2,
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    // boxShadow: { xs: 2, sm: 0 },
                    // border: { xs: "1px solid #e0e0e0", sm: "none" },
                    backgroundColor: { xs: "#fff", sm: "transparent" },
                  }}
                >
                  {/* Mobile delete button: top-left of the card */}
                  {isSmallDevice &&
                    values.monthlyIncome?.rentalAmounts?.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label={`remove rental ${index}`}
                        onClick={() => remove(index)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(0,0,0,0.04)",
                          zIndex: 5,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}

                  <DynamicFieldGroup
                    showDropdown={true}
                    dropdownOptions={rentalOptions}
                    dropdownValue={rental.type || ""}
                    onDropdownChange={(e) =>
                      setFieldValue(
                        `monthlyIncome.rentalAmounts[${index}].type`,
                        e.target.value,
                      )
                    }
                    dropdownLabel="Select Type"
                    showSlider={true}
                    sliderValue={
                      values.monthlyIncome?.rentalAmounts?.[index]?.amount || 0
                    }
                    onSliderChange={(e, newValue) => {
                      setFieldValue(
                        `monthlyIncome.rentalAmounts[${index}].amount`,
                        newValue,
                      );
                    }}
                    sliderMin={0}
                    sliderMax={1000000}
                    sliderStep={1000}
                    showInputField={true}
                    inputValue={
                      values.monthlyIncome?.rentalAmounts?.[index]?.amount || ""
                    }
                    onInputChange={(e) => {
                      const newValue = Number(e.target.value) || 0;
                      setFieldValue(
                        `monthlyIncome.rentalAmounts[${index}].amount`,
                        newValue,
                      );
                    }}
                    inputLabel="Amount ₹"
                    inputPlaceholder="Amount ₹"
                    inputType="number"
                    /* On mobile we render our own top-left delete, so hide inner remove */
                    showRemoveButton={
                      !isSmallDevice &&
                      values.monthlyIncome?.rentalAmounts?.length > 1
                    }
                    onRemove={() => remove(index)}
                    sx={
                      {
                        // borderBottom: "1px solid #ccc",
                      }
                    }
                  />
                </Box>
              ))}

              {/* Add Rental Button - positioned bottom-right on small screens */}
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: { xs: "flex-end", sm: "flex-end" },
                  // position: { xs: "absolute", sm: "static" },
                  right: { xs: 12, sm: "auto" },
                  bottom: { xs: 12, sm: "auto" },
                  zIndex: 10,
                }}
              >
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => push({ type: "", amount: 0 })}
                >
                  + Add Rental
                </button>
              </Box>
            </div>
          )}
        </FieldArray>
      </Paper>
    </Box>
  );
};

export default MonthlyIncome;
